import { Button, Card, cn } from '@shadcn/ui';
import { useGqlQueryVariables } from '../../hooks/use-gql-query-variables';
import { Sparkles, X, ThumbsUp, ThumbsDown } from 'lucide-react';
import {
    CREATE_CONTACTS_INTERACTIONS,
    CREATE_SEARCH_RESULT_INTERACTIONS,
    GET_CONTACT_BY_IDS,
    GET_CONTACTS_INTERACTIONS,
    GET_GROUPS,
    GET_PLAYBOOK_BY_GROUP_ID,
} from '@zaplify/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Textarea,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@shadcn/ui';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { boostWeightsFromGqlVariables } from '@andsend/intelligence-shared';
import { useAuth } from '../../../../providers/authentication-provider';

export const AgentFilterCard = ({
    className,
    resultCount = 0,
    onThumbsUp,
    onThumbsDown,
}: {
    className?: string;
    resultCount?: number;
    onThumbsUp?: () => void;
    onThumbsDown?: (feedback?: string) => void;
}) => {
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const { toast } = useToast();
    const {
        authState: {
            user: { id: userId, userOrganizationId: organizationId },
        },
    } = useAuth();
    const {
        setGqlQueryVariables,
        gqlQueryVariables,
        setActiveFilterId,
        userRequest,
        error,
        setUserRequest,
        setError,
        setFilterContactIds,
        filterContactIds,
        textSearchQuery,
        setTextSearchQuery,
        filterGroupIds,
        setFilterGroupIds,
    } = useGqlQueryVariables();

    const { data: contactProfiles, loading: contactProfilesLoading } = useQuery(GET_CONTACT_BY_IDS, {
        variables: {
            ids: filterContactIds,
        },
        skip: !filterContactIds?.length,
    });
    const { data: playbook, loading: playbookLoading } = useQuery(GET_PLAYBOOK_BY_GROUP_ID, {
        variables: {
            groupId: filterGroupIds?.[0],
        },
        skip: !filterGroupIds?.[0],
    });

    const [createSearchResultInteraction] = useMutation(CREATE_SEARCH_RESULT_INTERACTIONS);
    const contactNames = useMemo(() => {
        return contactProfiles?.Contacts?.map((contact) =>
            contact.fullName
                ?.split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        ).join(', ');
    }, [contactProfiles]);

    const handleRemoveFilter = () => {
        setGqlQueryVariables(undefined);
        setActiveFilterId(undefined);
        setUserRequest(undefined);
        setError(undefined);
        setFilterContactIds(undefined);
        setTextSearchQuery(undefined);
        setFilterGroupIds(undefined);
    };

    const handleThumbsDown = () => {
        setFeedbackDialogOpen(true);
    };

    const handleThumbsUp = () => {
        const weights = boostWeightsFromGqlVariables(gqlQueryVariables, 0.1);

        createSearchResultInteraction({
            variables: {
                userId: userId,
                organizationId: organizationId,
                metadata: {
                    gqlQueryVariables,
                    userRequest,
                },
                weights: weights,
                interactionType: 'SEARCH_RESULT_THUMBS_UP',
            },
            refetchQueries: [GET_CONTACTS_INTERACTIONS],
        });
        if (onThumbsUp) {
            onThumbsUp();
        }
        toast({
            variant: 'success',
            title: 'Thank you!',
            description: "I'll show you more of these in the future.",
        });
    };

    const handleSubmitFeedback = () => {
        const weights = boostWeightsFromGqlVariables(gqlQueryVariables, -0.1);

        createSearchResultInteraction({
            variables: {
                userId: userId,
                organizationId: organizationId,
                metadata: {
                    gqlQueryVariables,
                    userRequest,
                    feedback: feedbackText,
                },
                weights: weights,
                interactionType: 'SEARCH_RESULT_THUMBS_DOWN',
            },
            refetchQueries: [GET_CONTACTS_INTERACTIONS],
        });
        if (onThumbsDown) {
            onThumbsDown(feedbackText);
        }
        setFeedbackDialogOpen(false);
        setFeedbackText('');
        toast({
            variant: 'destructive',
            title: 'Thank you!',
            description: "I'll show you less of these in the future.",
        });
    };

    const subtitle = useMemo(() => {
        if (filterContactIds && filterContactIds.length > 0) {
            if (filterContactIds.length === 1) {
                return (
                    <span>
                        {`Displaying actions for `}
                        <strong>{contactNames}</strong>
                    </span>
                );
            } else {
                return (
                    <span>
                        {`Displaying actions for `}
                        <strong>{filterContactIds.length} contacts</strong>
                    </span>
                );
            }
        }
        if (textSearchQuery) {
            return (
                <span>
                    {`Displaying actions for `}
                    <strong>"{textSearchQuery}"</strong>
                </span>
            );
        }
        if (playbook?.Groups[0]) {
            return (
                <span>
                    {`Displaying actions for `}
                    <strong>{playbook?.Groups[0]?.name}</strong>
                </span>
            );
        }
        return userRequest || 'These actions are based on your filter';
    }, [filterContactIds, contactNames, userRequest, textSearchQuery, playbook]);

    return (
        <>
            <Card
                className={cn(
                    'w-full flex flex-col gap-2 p-4 bg-gradient-to-b from-background-brand-secondary-section/50 to-background-primary relative',
                    className
                )}
            >
                <div className="flex flex-row gap-2 items-center">
                    <Sparkles className="w-4 h-4 text-brand-secondary" />
                    <span className="font-medium">{`Found ${resultCount} actions`} </span>

                    {!(filterContactIds?.length > 0) && !textSearchQuery && !(filterGroupIds?.length > 0) && (
                        <div className="absolute right-4 top-4 flex gap-0">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 hover:bg-transparent group"
                                            onClick={handleThumbsUp}
                                        >
                                            <ThumbsUp className="h-4 w-4 text-muted-foreground transition-colors duration-300 group-hover:fill-background-brand-primary/20" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Teach the AI this was a good search result</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 hover:bg-transparent group"
                                            onClick={handleThumbsDown}
                                        >
                                            <ThumbsDown className="h-4 w-4 text-muted-foreground transition-colors duration-300 group-hover:fill-background-brand-primary/20" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Teach the AI this was a bad search result</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    )}
                </div>
                <p className="text-sm text-muted-foreground">{subtitle}</p>
                <div className="flex flex-row gap-2 w-full justify-end">
                    <Button variant="outline" className="flex gap-2" onClick={handleRemoveFilter}>
                        <X className="w-4 h-4" />
                        Remove filter
                    </Button>
                </div>
            </Card>

            <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Provide Feedback</DialogTitle>
                        <DialogDescription>
                            Tell us why this search result wasn't helpful. Your feedback helps improve our AI.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <Textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="What was wrong with this search result?"
                            className="min-h-[100px]"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFeedbackDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmitFeedback}>Submit Feedback</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
